import { ComponentProps, forwardRef } from 'react';
import { withStyles } from '../../../utils';
import styles from './UtilButton.module.scss';
import MaterialSymbol from '../../DataDisplay/MaterialSymbol/MaterialSymbol';
import { useDatadogLog } from '../logging';

const cn = withStyles(styles);

type Size = 'large' | 'medium' | 'small';
type IconProps = ComponentProps<typeof MaterialSymbol>;

type UtilButtonProps = React.ComponentProps<'button'> & {
  size?: Size;
  iconLeft?: IconProps['icon'];
  iconLeftProps?: IconProps;
  iconRight?: IconProps['icon'];
  iconRightProps?: IconProps;
  active?: boolean;
  'data-testid'?: string;
};

const UtilButton = forwardRef<HTMLButtonElement, UtilButtonProps>(
  (
    {
      children,
      size = 'medium',
      className,
      style,
      iconLeft,
      iconLeftProps,
      iconRight,
      iconRightProps,
      active = false,
      'data-testid': dataTestId = 'util-button',
      ...props
    },
    forwardedRef,
  ) => {
    const log = useDatadogLog();

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      const label =
        typeof children === 'string' ? children : (props['aria-label'] ?? e.currentTarget.textContent ?? 'Button');

      log({
        message: `${label} clicked`,
        payload: { name: label },
        actionType: 'Button',
      });

      if (props.onClick) {
        props.onClick(e);
      }
    };

    return (
      <button
        ref={forwardedRef}
        data-testid={dataTestId}
        type="button"
        {...props}
        className={cn(
          'utilButton',
          active && 'active',
          'color-default',
          `size-${size}`,
          (iconLeftProps || iconLeft) && 'iconLeft',
          (iconRightProps || iconRight) && 'iconRight',
          className,
        )}
        style={style}
        onClick={handleClick}
        data-custom="true"
      >
        {iconLeftProps && <MaterialSymbol {...iconLeftProps} className={cn('icon', iconLeftProps.className)} />}
        {iconLeft && <MaterialSymbol className={cn('icon')} icon={iconLeft} />}
        {children}
        {iconRight && <MaterialSymbol className={cn('icon')} icon={iconRight} />}
        {iconRightProps && <MaterialSymbol {...iconRightProps} className={cn('icon', iconRightProps.className)} />}
      </button>
    );
  },
);

UtilButton.displayName = 'UtilButton';

export default UtilButton;
